import { computed, inject, Injectable, resource } from '@angular/core';

import { SessionStore } from '../core/services/session.store';

import { LegalDocumentsGateway } from './legal-documents.gateway';
import { MANDATORY_LEGAL_DOCUMENTS } from './MandatoryLegalDocuments';
import { LegalDocument } from './model/LegalDocument';
import { LegalDocumentApproval } from './model/LegalDocumentApproval';
import { LEGAL_DOCUMENTS_TYPES } from './model/LegalDocumentType';
import { toDocumentLanguage, toDocumentsToSign, toLastApproval } from './services/legalDocumentMapping';

/**
 * The installer's legal documents: those still to sign and the last approval.
 * Both are loaded once an installer is signed in, in the language of the moment, and emptied when the session ends.
 */
@Injectable({ providedIn: 'root' })
export class LegalDocumentsStore {
    private readonly gateway = inject(LegalDocumentsGateway);
    private readonly session = inject(SessionStore);

    private readonly country = computed(() => this.session.user()?.country);

    private readonly lastApproval = resource({
        params: () => this.country(),
        loader: ({ params }) => this.fetchLastApproval(params),
    });

    private readonly toSign = resource({
        params: () => this.country(),
        loader: async ({ params }) =>
            toDocumentsToSign(await this.gateway.getDocumentsToApprove(params), this.language()),
    });

    /** Empty while loading, outside a session, or when the request failed. */
    readonly documentsToSign = computed<LegalDocument[]>(() => (this.toSign.hasValue() ? this.toSign.value() : []));

    /** The signed mandatory documents, listed in the side menu. */
    readonly approvedDocuments = computed<LegalDocumentApproval | undefined>(() => {
        const approval = this.approvalOrNull();
        return approval
            ? {
                  approvalDate: approval.approvalDate,
                  approvedDocuments: approval.approvedDocuments.filter((document) =>
                      MANDATORY_LEGAL_DOCUMENTS.includes(document.documentType),
                  ),
              }
            : undefined;
    });

    readonly legalNoticeUrl = computed<string | undefined>(
        () =>
            this.approvalOrNull()?.approvedDocuments.find(
                (document) => document.documentType === LEGAL_DOCUMENTS_TYPES.SPECIAL_TERMS,
            )?.url,
    );

    /** Signs every pending document at once; rejects, keeping them pending, when the backend refuses. */
    async approve(): Promise<void> {
        const { country } = this.session.signedInInstaller();
        await this.gateway.approve(country);
        this.toSign.set([]);

        try {
            // An approval that cannot be read back keeps the one shown until the next load.
            const approval = await this.fetchLastApproval(country);
            if (approval) this.lastApproval.set(approval);
        } catch {
            // The signing itself succeeded: nothing to report.
        }
    }

    private async fetchLastApproval(country: string): Promise<LegalDocumentApproval | null> {
        return toLastApproval(await this.gateway.getApprovals(country), this.language());
    }

    private approvalOrNull(): LegalDocumentApproval | null {
        return this.lastApproval.hasValue() ? this.lastApproval.value() : null;
    }

    private language(): string {
        return toDocumentLanguage(this.session.language());
    }
}
